'use client';
import type React from 'react';
import { cloneElement, createContext, useCallback, useContext, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import { v4 as uuid } from 'uuid';
import { useClientOnlyConstant } from '../../hooks/useClientOnlyConstant';
import {
  type BaseLinkEvent,
  EventName,
  type LinkClickedPayload,
  type LinkViewedPayload,
} from '../../Shibuya';
import { useAnalytics } from '..';
import { useAnalyticsPageView } from '../AnalyticsPageView';
import { getCleanLocationHref } from '../urlUtils';

export interface LinkViewedExtra {
  entityName?: string;
  linkPosition?: BaseLinkEvent['link_position'];
  linkCopy?: BaseLinkEvent['link_copy'];
  destinationContentId?: BaseLinkEvent['destination_content_id'];
  additionalAttributes?: BaseLinkEvent['additional_attributes'];
}

export interface LinkViewedContextValue {
  eventId: string;
  fireLinkClicked: (extra?: LinkViewedExtra) => void;
}

const LinkViewedContext = createContext<LinkViewedContextValue | null>(null);

export interface LinkViewedProps {
  entityName: string;
  sectionName?: string;
  pageType?: string;
  destinationUrl: string;
  linkPosition?: BaseLinkEvent['link_position'];
  linkCopy?: BaseLinkEvent['link_copy'];
  destinationContentId?: BaseLinkEvent['destination_content_id'];
  additionalAttributes?: BaseLinkEvent['additional_attributes'];
  children?: React.JSX.Element | ((context: LinkViewedContextValue) => React.JSX.Element);
}

/**
 * Fires a paired `Link Viewed` / `Link Clicked` sequence for one link element.
 * `Link Viewed` fires once the wrapped child enters the viewport; `fireLinkClicked`
 * (exposed via context) threads the view's id through as `parent_event_id`.
 */
export const LinkViewed = ({
  children,
  entityName,
  sectionName,
  pageType,
  destinationUrl,
  linkPosition,
  linkCopy,
  destinationContentId,
  additionalAttributes,
}: LinkViewedProps) => {
  const { analytics, deployableName, deployableVersion, deviceId, gitSha } = useAnalytics();
  const { pageViewId, pageName, pageViewReady } = useAnalyticsPageView();
  const eventId = useClientOnlyConstant(() => uuid(), '');
  const { ref: inViewRef, inView } = useInView({
    root: null,
    rootMargin: '1px 0px 0px 0px',
    threshold: 0,
    triggerOnce: true,
  });

  const viewed = pageViewReady && !!deviceId && inView;

  useEffect(() => {
    if (!viewed) return;
    const payload: LinkViewedPayload = {
      deployable_name: deployableName,
      deployable_version: deployableVersion,
      event_id: eventId,
      entity_name: entityName,
      section_name: sectionName,
      pageview_id: pageViewId,
      page_name: pageName,
      page_url: getCleanLocationHref(),
      destination_url: destinationUrl,
      destination_content_id: destinationContentId,
      link_position: linkPosition,
      link_copy: linkCopy,
      additional_attributes: {
        traditional_device_id: deviceId as string,
        ...(pageType && { page_type: pageType }),
        ...additionalAttributes,
      },
    };
    analytics?.track(EventName.LINK_VIEWED, payload, {
      context: { app: { name: deployableName, version: gitSha } },
    });
  }, [viewed]);

  const fireLinkClicked = useCallback(
    (extra: LinkViewedExtra = {}) => {
      if (!pageViewReady || !deviceId) return;
      const payload: LinkClickedPayload = {
        deployable_name: deployableName,
        deployable_version: deployableVersion,
        event_id: uuid(),
        parent_event_id: eventId,
        entity_name: extra.entityName ?? entityName,
        section_name: sectionName,
        pageview_id: pageViewId,
        page_name: pageName,
        page_url: getCleanLocationHref(),
        destination_url: destinationUrl,
        destination_content_id: extra.destinationContentId ?? destinationContentId,
        link_position: extra.linkPosition ?? linkPosition,
        link_copy: extra.linkCopy ?? linkCopy,
        additional_attributes: {
          traditional_device_id: deviceId,
          ...(pageType && { page_type: pageType }),
          ...additionalAttributes,
          ...extra.additionalAttributes,
        },
      };
      analytics?.track(EventName.LINK_CLICKED, payload, {
        context: { app: { name: deployableName, version: gitSha } },
      });
    },
    [
      analytics,
      additionalAttributes,
      deviceId,
      deployableName,
      deployableVersion,
      gitSha,
      eventId,
      viewed,
      entityName,
      sectionName,
      pageType,
      destinationUrl,
      destinationContentId,
      linkPosition,
      linkCopy,
      pageViewId,
      pageName,
      pageViewReady,
    ],
  );

  const context: LinkViewedContextValue = { eventId, fireLinkClicked };
  const child = typeof children === 'function' ? children(context) : children;
  const childRef = child ? ((child.props as { ref?: React.Ref<Element> }).ref ?? null) : null;
  const composedRef = useCallback(
    (node: Element | null): void | (() => void) => {
      inViewRef(node);
      if (typeof childRef === 'function') {
        return childRef(node);
      }
      if (childRef !== null) {
        (childRef as React.MutableRefObject<Element | null>).current = node;
      }
    },
    [inViewRef, childRef],
  );
  if (!child) {
    throw new Error('Missing Child');
  }
  return (
    <LinkViewedContext.Provider value={context}>
      {cloneElement(child, { ref: composedRef })}
    </LinkViewedContext.Provider>
  );
};

export const useLinkViewedContext = () => {
  const context = useContext(LinkViewedContext);
  if (!context) {
    throw new Error('useLinkViewedContext must be used within LinkViewed');
  }
  return context;
};
